import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { AppLang } from "../../i18n";

export type LanguageOption = {
  value: AppLang;
  label: string;
};

export type LanguageSwitcherProps = {
  allowedLanguages: readonly AppLang[];
  currentLanguage: AppLang;
  onLanguageChange: (language: AppLang) => void | Promise<void>;
  /** When true, buttons are disabled (e.g. persistence in flight). */
  disabled?: boolean;
  className?: string;
};

const LABEL_KEY: Record<AppLang, string> = {
  en: "common.english",
  ta: "common.tamil",
  ms: "common.malay",
};

const FALLBACK_LABEL: Record<AppLang, string> = {
  en: "English",
  ta: "தமிழ்",
  ms: "Bahasa Melayu",
};

/**
 * Modern segmented language control for portal headers.
 * Renders options dynamically from `allowedLanguages` — never hardcodes the set.
 */
export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  allowedLanguages,
  currentLanguage,
  onLanguageChange,
  disabled = false,
  className = "",
}) => {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Map<AppLang, HTMLButtonElement>>(new Map());
  const [pill, setPill] = useState({ left: 0, width: 0, ready: false });

  const options: LanguageOption[] = useMemo(
    () =>
      allowedLanguages.map((code) => ({
        value: code,
        label: t(LABEL_KEY[code], { defaultValue: FALLBACK_LABEL[code] }),
      })),
    [allowedLanguages, t]
  );

  const updatePill = useCallback(() => {
    const btn = buttonRefs.current.get(currentLanguage);
    const track = trackRef.current;
    if (!btn || !track) {
      setPill((p) => ({ ...p, ready: false }));
      return;
    }
    setPill({
      left: btn.offsetLeft,
      width: btn.offsetWidth,
      ready: true,
    });
  }, [currentLanguage]);

  useLayoutEffect(() => {
    updatePill();
  }, [updatePill, options]);

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => updatePill());
    ro.observe(track);
    return () => ro.disconnect();
  }, [updatePill]);

  const groupLabel = t("common.language", { defaultValue: "Language" });

  if (options.length < 2) return null;

  return (
    <div
      className={`inline-flex h-[42px] max-w-full items-center gap-1 rounded-full border border-white/20 bg-white/[0.08] px-1.5 shadow-[0_2px_12px_rgba(0,0,0,0.12)] backdrop-blur-md sm:gap-1.5 sm:px-1.5 ${className}`}
      role="group"
      aria-label={groupLabel}
    >
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center text-white/90 sm:h-8 sm:w-8"
        aria-hidden
      >
        <Globe className="h-[18px] w-[18px] sm:h-4 sm:w-4" strokeWidth={2.25} />
      </span>

      <div className="min-w-0 flex-1 overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div
          ref={trackRef}
          className="relative inline-flex flex-nowrap items-center gap-0.5 py-0.5 sm:gap-1"
        >
          {pill.ready ? (
            <span
              aria-hidden
              className="pointer-events-none absolute top-0.5 bottom-0.5 rounded-full bg-white/95 shadow-[0_2px_8px_rgba(0,0,0,0.14)] transition-[left,width] duration-[220ms] ease-in-out"
              style={{ left: pill.left, width: pill.width }}
            />
          ) : null}

          {options.map((opt) => {
            const selected = currentLanguage === opt.value;
            return (
              <button
                key={opt.value}
                ref={(el) => {
                  if (el) buttonRefs.current.set(opt.value, el);
                  else buttonRefs.current.delete(opt.value);
                }}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={`${groupLabel}: ${opt.label}`}
                disabled={disabled}
                onClick={() => {
                  if (selected || disabled) return;
                  void onLanguageChange(opt.value);
                }}
                className={`relative z-[1] min-h-[40px] shrink-0 whitespace-nowrap rounded-full px-3 py-2 text-xs font-bold transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent disabled:cursor-not-allowed disabled:opacity-60 sm:px-3.5 sm:text-[13px] ${
                  selected
                    ? "text-app-primary"
                    : "text-white/70 hover:bg-white/10 hover:text-white/90"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
